import React, {
  useRef,
  useEffect,
  PropsWithChildren,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { CSSObject } from '@emotion/styled';
import {
  WHITE,
  BLACK_10,
  mq,
} from '@/ui-kit/theme';
import merge from 'lodash.merge';
import { DialogContextProvider } from './dialog-context';
import { Body } from './body';
import { Overlay } from './overlay';
import { DialogSize } from './constants';

interface IStyledDialogProps {
  size?: DialogSize;
}

const StyledDialog = styled.aside<IStyledDialogProps>(({ size }) => {
  let styles: CSSObject = {
    zIndex: 101,
    position: 'fixed',
    top: '50%',
    left: '50%',
    translate: '-50% -50%',
    display: 'flex',
    overflowY: 'auto',
    flexDirection: 'column',
    borderRadius: '24px',
    background: WHITE,
    boxShadow: `0px 0px 0px 1px ${BLACK_10}, 0px 0px 16px 0px ${BLACK_10}`,
    [mq.xs]: {
      width: 'calc(100dvw - 16px)',
      borderRadius: '12px',
    },
  };

  if (size === DialogSize.TINY) {
    const largeStyles: CSSObject = { width: '600px' };
    styles = merge(styles, largeStyles);
  }

  if (size === DialogSize.SMALL) {
    const smallStyles: CSSObject = { width: '400px' };
    styles = merge(styles, smallStyles);
  }

  if (size === DialogSize.LARGE) {
    const largeStyles: CSSObject = {
      width: '600px',
      [mq.sm]: { width: 'calc(100dvw - 16px)' },
    };
    styles = merge(styles, largeStyles);
  }

  if (size === DialogSize.FULL_SCREEN) {
    const fullScreenStyles: CSSObject = { width: 'calc(100dvw - 16px)' };
    styles = merge(styles, fullScreenStyles);
  }

  return styles;
});

interface IDialogProps extends PropsWithChildren {
  className?: string;
  isVisible?: boolean;
  isSelfClosingDisabled?: boolean;
  size?: DialogSize;
  onClose: () => void;
}

export const Dialog = ({
  className,
  isVisible = false,
  isSelfClosingDisabled = false,
  onClose,
  children,
  size = DialogSize.SMALL,
}: IDialogProps) => {
  const closeHandler = useRef(onClose);

  useEffect(() => {
    closeHandler.current = onClose;
  }, [onClose]);

  const handleClose = () => {
    closeHandler.current();
  };

  return (
    <>
      {createPortal(
        <DialogContextProvider
          isVisible={isVisible}
          isSelfClosingDisabled={isSelfClosingDisabled}
          size={size}
          onClose={handleClose}
        >
          <Overlay />
          <StyledDialog
            className={className}
            size={size}
          >
            {children}
          </StyledDialog>
        </DialogContextProvider>,
        document.body,
      )}
    </>
  );
};

Dialog.Body = Body;
